import { FC, memo } from 'react';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';

import { Title, Feature, Table } from './features.styles';
import { PropertyFeature } from '@keaze/web/common/interfaces';
import { useFeatures } from './features.hook';
import { FeaturesProps, TableData } from './features.types';

type RenderPropertyFeature = (value: string) => JSX.Element;

type RenderPropertyFeatureValues = (property: PropertyFeature) => JSX.Element[];

type RenderTableRow = (data: TableData) => JSX.Element | null;

type RenderTable = (
  bodyRows1: TableData[],
  bodyRows2: TableData[]
) => JSX.Element;

const renderPropertyFeature: RenderPropertyFeature = (value: string) => (
  <Feature key={value}>{value}</Feature>
);

const renderPropertyFeatureValues: RenderPropertyFeatureValues = ({ values }) =>
  values.map(renderPropertyFeature);

const renderTableRow: RenderTableRow = ({ name, value, hidden }) => {
  if (hidden) {
    return null;
  }

  return (
    <TableRow key={name}>
      <TableCell>{name}</TableCell>
      <TableCell align="right">{value}</TableCell>
    </TableRow>
  );
};

export const renderTable: RenderTable = (bodyRows1, bodyRows2) => (
  <Table aria-label="features table">
    <TableBody>{bodyRows1.map(renderTableRow)}</TableBody>
    <TableBody>{bodyRows2.map(renderTableRow)}</TableBody>
  </Table>
);

export const Features: FC<FeaturesProps> = memo(
  ({ propertyFeatures, propertyDetails }) => {
    const { bodyRows1, bodyRows2 } = useFeatures(propertyDetails);
    const isVisiblePropertyFeatures = propertyFeatures.length !== 0;

    return (
      <Box mt={3}>
        <Title variant="h2">Features</Title>
        {isVisiblePropertyFeatures && (
          <Grid container>
            {propertyFeatures.map(renderPropertyFeatureValues)}
          </Grid>
        )}
        {renderTable(bodyRows1, bodyRows2)}
      </Box>
    );
  }
);
